import { useMemo, useState } from "react";
import { Button, Checkbox, Col, Divider, Flex, Row, Select } from "antd";
import ListInput from "./components/Input";
import ListSelect from "./components/Select";
import ListDatePicker from "./components/DatePicker";
import NumericInput from "./components/NumericInput";
import { name_regexp, special_characters_regexp } from "helpers/regexp";
import { useSettlement } from "context/providers/settlement";
import RangePicker from "./components/RangePicker";

const getRegexp = (name) => {
  switch (name) {
    case "name":
    case "first_name":
    case "last_name":
      return name_regexp;
    case "ip_address":
      return new RegExp(/^[0-9.]{0,20}$/);
    case "post_code":
      return new RegExp(/^[0-9-]{0,6}$/);
    default:
      return special_characters_regexp;
  }
};

const FilterComponent = ({
  search_values,
  title,
  dataIndex: name,
  search_component = "input",
  search_type = "text",
  search_method,
  options = [],
  selectTagRender,
  handleSetSearchValues,
  onSearch,
}) => (
  <>
    {search_component === "input" &&
      (search_type === "number" ? (
        <NumericInput
          search_type={search_type}
          title={title}
          value={search_values?.[name] || ""}
          onChange={(value) =>
            handleSetSearchValues({
              ...search_values,
              [name]: value,
            })
          }
          onBlur={() =>
            onSearch({
              name,
              search_method,
              value: search_values?.[name],
            })
          }
          onPressEnter={() =>
            onSearch({
              name,
              search_method,
              value: search_values?.[name],
            })
          }
        />
      ) : (
        <ListInput
          search_type={search_type}
          regexp={getRegexp(name)}
          title={title}
          value={search_values?.[name] || ""}
          onChange={(value) =>
            handleSetSearchValues({
              ...search_values,
              [name]: value,
            })
          }
          onBlur={() =>
            onSearch({
              name,
              search_method,
              value: search_values?.[name],
            })
          }
          onPressEnter={() =>
            onSearch({
              name,
              search_method,
              value: search_values?.[name],
            })
          }
        />
      ))}
    {search_component === "select" && (
      <ListSelect
        title={title}
        options={options}
        value={search_values?.[name]}
        selectTagRender={selectTagRender}
        onChange={(value) => [
          handleSetSearchValues({
            ...search_values,
            [name]: value,
          }),
          onSearch({
            name,
            search_method,
            value,
          }),
        ]}
      />
    )}
    {search_component === "date-picker" && (
      <ListDatePicker
        title={title}
        value={search_values?.[name]}
        onChange={(value) => [
          handleSetSearchValues({
            ...search_values,
            [name]: value,
          }),
          onSearch({
            name,
            search_method,
            value,
          }),
        ]}
      />
    )}
    {search_component === "range-picker" && (
      <RangePicker
        title={title}
        value={search_values?.[name]}
        onChange={(value) => [
          handleSetSearchValues({
            ...search_values,
            [name]: value,
          }),
          onSearch({
            name,
            search_method,
            value,
          }),
        ]}
      />
    )}
  </>
);

const FilterComponents = ({
  columns,
  search_values,
  handleSetSearchValues,
  onSearch,
}) =>
  columns
    .filter(({ searchable }) => searchable)
    .map((item) =>
      item.is_important ? (
        <div style={{ flexBasis: "300px", maxWidth: "300px" }}>
          <FilterComponent
            search_values={search_values}
            onSearch={onSearch}
            handleSetSearchValues={handleSetSearchValues}
            {...item}
          />
        </div>
      ) : (
        <Col xs={24} md={12} lg={6} xl={4}>
          <FilterComponent
            search_values={search_values}
            onSearch={onSearch}
            handleSetSearchValues={handleSetSearchValues}
            {...item}
          />
        </Col>
      )
    );

const IssueFilters = () => {
  const [show_columns, setShowColumns] = useState(false);
  const {
    is_changed,
    columns,
    query,
    setQuery,
    search_values,
    handleSetSearchValues,
    paginate,
    setPaginate,
    handleReset,
    checked_list,
    updateCheckedList,
    checked_list_options,
  } = useSettlement();

  const onSearch = ({ name, value, search_method }) =>
    setQuery({
      ...query,
      [name]: { value, search_method },
    });

  const sort_options = useMemo(() => {
    const data = [];
    columns
      .filter(({ sortable }) => sortable)
      .forEach(({ dataIndex, title }) => {
        data.push({
          label: `${title} malejąco`,
          value: `${dataIndex}__desc`,
        });
        data.push({
          label: `${title} rosnąco`,
          value: `${dataIndex}__asc`,
        });
      });
    return data;
  }, [columns]);

  const important_columns = useMemo(
    () => columns.filter(({ is_important = false }) => is_important),
    [columns]
  );

  const standard_columns = useMemo(
    () => columns.filter(({ is_important = false }) => !is_important),
    [columns]
  );

  return (
    <>
      <Row gutter={[12, 12]}>
        <FilterComponents
          columns={standard_columns}
          search_values={search_values}
          onSearch={onSearch}
          handleSetSearchValues={handleSetSearchValues}
        />
      </Row>
      <Flex gap={12} wrap className="m-t-20 m-b-20">
        <Flex align="center" wrap gap={12} style={{ flex: 1 }}>
          <Select
            allowClear
            style={{
              minWidth: 240,
            }}
            placeholder="Sortuj"
            value={sort_options.find(({ value }) => value === paginate?.sort)}
            onChange={(value) => {
              setPaginate({
                ...paginate,
                page: undefined,
                sort: value,
              });
            }}
            options={sort_options}
          />
          <FilterComponents
            columns={important_columns}
            search_values={search_values}
            onSearch={onSearch}
            handleSetSearchValues={handleSetSearchValues}
          />
        </Flex>
        <Flex gap={12} wrap align="center">
          <Button type="primary">Szukaj</Button>
          <Button onClick={handleReset} disabled={is_changed}>
            Resetuj
          </Button>
          <Button onClick={() => setShowColumns(!show_columns)}>
            {!show_columns ? "Pokaż" : "Ukryj"} kolumny
          </Button>
        </Flex>
      </Flex>
      {show_columns && (
        <>
          <Divider />
          <Checkbox.Group
            value={checked_list}
            options={checked_list_options}
            onChange={updateCheckedList}
          />
          <Divider />
        </>
      )}
    </>
  );
};

export default IssueFilters;
